// src/enums.ts
var tuple = (...values) => values;
var ROLES = tuple("OWNER", "MANAGER", "OPERATOR", "SALES", "BUYER");
var STAGE_KINDS = tuple("TRANSFORM", "TEST", "PACK");
var STAGE_ICONS = tuple(
  "RAW",
  "ROD_BREAKDOWN",
  "WIRE_DRAWING",
  "ANNEALING",
  "STRANDING",
  "INSULATION",
  "CABLING",
  "SHEATHING",
  "TESTING",
  "PACKAGING"
);
var MEASURE_UNITS = tuple("KG", "M", "PCS");
var CONTAINER_KINDS = ["BASKET", "BOBBIN", "OTHER"];
var MATERIAL_ROLES = tuple("COPPER", "COMPOUND", "FILLER", "PACKAGING", "OTHER");
var INSULATION_MATERIALS = tuple("PVC", "XLPE");
var SALE_KINDS = tuple("PACKAGED", "CUT_LENGTH", "IN_PROCESS");
var WORK_ORDER_STATUSES = tuple("DRAFT", "RELEASED", "IN_PROGRESS", "DONE", "CANCELLED");
var SHIFTS = tuple("MORNING", "AFTERNOON", "NIGHT");
var WIP_LOT_STATUSES = tuple("AVAILABLE", "CONSUMED", "ON_HOLD", "SCRAPPED");
var QC_STATUSES = tuple("PENDING", "PASS", "FAIL");
var QC_RESULTS = tuple("PASS", "FAIL");
var STOCK_STATUSES = tuple("IN_STOCK", "RESERVED", "DELIVERED", "SCRAPPED", "MISSING");
var MOVEMENT_TYPES = tuple(
  "RECEIPT",
  "CONSUMPTION",
  "PRODUCTION",
  "SCRAP",
  "ADJUSTMENT",
  "RESERVATION",
  "RESERVATION_RELEASE",
  "DELIVERY",
  "RETURN",
  "REVALUATION"
);
var STOCK_ITEM_KINDS = tuple("MATERIAL_LOT", "WIP_LOT", "FINISHED_UNIT");
var STOCK_COUNT_STATUSES = tuple("OPEN", "POSTED", "CANCELLED");
var STOCK_COUNT_OUTCOMES = tuple("MATCH", "ADJUSTED", "MISSING", "FOUND", "ANOMALY");
var SALES_ORDER_STATUSES = tuple(
  "DRAFT",
  "CONFIRMED",
  "PARTIALLY_DELIVERED",
  "DELIVERED",
  "CANCELLED"
);
var PRICE_UNITS = tuple("PER_METER", "PER_UNIT", "PER_KG");
var DOCUMENT_STATUSES = tuple("DRAFT", "VALIDATED", "CANCELLED");
var PROFORMA_STATUSES = tuple("DRAFT", "ISSUED", "ACCEPTED", "EXPIRED", "CANCELLED");
var PAYMENT_STATUSES = tuple("UNPAID", "PARTIAL", "PAID");
var SCRAP_MATERIALS = tuple("COPPER", "COMPOUND", "MIXED");

// src/constants.ts
var TIMEZONE = "Africa/Algiers";
var DATE_FORMAT = "dd/MM/yyyy";
var DATETIME_FORMAT = "dd/MM/yyyy HH:mm";
var CURRENCY = "DZD";
var DEFAULT_LOCALE = "fr";
var SUPPORTED_LOCALES = ["fr", "ar"];
var RTL_LOCALES = ["ar"];
var SCALE = {
  money: 2,
  unitPrice: 4,
  kg: 3,
  m: 1,
  pct: 2
};
var DEFAULT_VAT_RATE = "19.00";
var DEFAULT_COPPER_VARIANCE_THRESHOLD_PCT = "1.50";
var STAFF_ROLES = ["OWNER", "MANAGER", "OPERATOR", "SALES"];
var PORTAL_ROLES = ["BUYER"];

// src/decimal.ts
import Decimal from "decimal.js";
var D = (v) => new Decimal(v);
var round = (v, dp) => D(v).toDecimalPlaces(dp, Decimal.ROUND_HALF_UP);
var roundMoney = (v) => round(v, SCALE.money);
var roundKg = (v) => round(v, SCALE.kg);
var roundM = (v) => round(v, SCALE.m);
var sum = (values) => values.reduce((acc, v) => acc.plus(v), new Decimal(0));
var toFixedString = (v, dp) => round(v, dp).toFixed(dp);

// src/format.ts
var groupThousands = (intPart, sep) => intPart.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
function formatDecimalFr(value, dp) {
  const fixed = round(value, dp).toFixed(dp);
  const negative = fixed.startsWith("-");
  const [intPart = "0", fracPart] = (negative ? fixed.slice(1) : fixed).split(".");
  const body = groupThousands(intPart, "\u202F") + (fracPart ? `,${fracPart}` : "");
  return negative ? `-${body}` : body;
}
var formatDzd = (v) => `${formatDecimalFr(v, 2)} DA`;
var formatKg = (v) => `${formatDecimalFr(v, 3)} kg`;
var formatM = (v) => `${formatDecimalFr(v, 1)} m`;
var dateParts = (date) => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23"
  }).formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value ?? "";
  return {
    dd: get("day"),
    MM: get("month"),
    yyyy: get("year"),
    HH: get("hour"),
    mm: get("minute")
  };
};
function formatDate(value) {
  const p = dateParts(new Date(value));
  return `${p.dd}/${p.MM}/${p.yyyy}`;
}
function formatDateTime(value) {
  const p = dateParts(new Date(value));
  return `${p.dd}/${p.MM}/${p.yyyy} ${p.HH}:${p.mm}`;
}
function inProcessLabel(l) {
  const d = l.wireDiameterMm != null ? ` \xD8 ${formatDecimalFr(l.wireDiameterMm.toString(), 2)} mm` : "";
  return `${l.stageLabel}${d}${l.productName ? ` \u2014 ${l.productName}` : ""}`;
}

// src/numbering.ts
var DEFAULT_NUMBER_PATTERN = "{PREFIX}-{YEAR}-{N}";
var DEFAULT_NUMBER_DIGITS = 5;
function formatNumberPattern(pattern, v) {
  return pattern.replaceAll("{PREFIX}", v.prefix).replaceAll("{YEAR}", String(v.year)).replaceAll("{YY}", String(v.year % 100).padStart(2, "0")).replaceAll("{N}", String(v.n).padStart(v.digits, "0"));
}
function numberPatternProblem(pattern) {
  if (!pattern.includes("{N}")) return "Le mod\xE8le doit contenir {N} (le num\xE9ro)";
  if (!pattern.includes("{YEAR}") && !pattern.includes("{YY}"))
    return "Le mod\xE8le doit contenir {YEAR} ou {YY} (le compteur repart \xE0 1 chaque ann\xE9e)";
  const rest = pattern.replace(/\{(PREFIX|YEAR|YY|N)\}/g, "");
  if (/[{}]/.test(rest)) return "Codes possibles : {PREFIX}, {YEAR}, {YY}, {N}";
  if (!/^[A-Za-z0-9 ./_°#-]*$/.test(rest))
    return "Caract\xE8res autoris\xE9s : lettres, chiffres, espace, - / . _ \xB0 #";
  return null;
}

// src/schemas/common.ts
import { z } from "zod";
var decimalString = (dp) => z.union([z.string(), z.number()]).transform((v) => String(v).trim().replace(",", ".")).refine((v) => new RegExp(`^\\d+(\\.\\d{1,${dp}})?$`).test(v), {
  message: `Nombre d\xE9cimal positif attendu (max ${dp} d\xE9cimales)`
});
var kgString = decimalString(3);
var meterString = decimalString(1);
var moneyString = decimalString(2);
var unitPriceString = decimalString(4);
var pctString = decimalString(2);
var idSchema = z.uuid();
var refCode = z.string().trim().min(1).max(30).regex(/^[A-Z0-9_]+$/, "Code : majuscules, chiffres et _");
var refItemFields = {
  code: z.preprocess(
    (v) => typeof v === "string" && v.trim() !== "" ? v.trim().toUpperCase() : void 0,
    refCode.optional()
  ),
  label: z.string().trim().min(1).max(80),
  labelAr: z.string().trim().max(80).nullish(),
  active: z.boolean().default(true),
  sortOrder: z.coerce.number().int().min(0).max(9999).default(0)
};
var pageFields = {
  page: z.coerce.number().int().min(1).max(1e5).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25)
};
var DEFAULT_PAGE_SIZE = 25;
var paginationQuerySchema = z.object({
  ...pageFields,
  search: z.string().trim().max(100).optional()
});

// src/schemas/auth.ts
import { z as z2 } from "zod";
var loginSchema = z2.object({
  email: z2.email().trim().toLowerCase(),
  password: z2.string().min(1).max(200)
});
var passwordSchema = z2.string().min(10, "Au moins 10 caract\xE8res").max(200).regex(/[A-Za-z]/, "Doit contenir une lettre").regex(/\d/, "Doit contenir un chiffre");
var changePasswordSchema = z2.object({
  currentPassword: z2.string().min(1),
  newPassword: passwordSchema
});
var authUserSchema = z2.object({
  id: z2.string(),
  email: z2.string(),
  fullName: z2.string(),
  role: z2.enum(ROLES),
  locale: z2.string(),
  customerId: z2.string().nullable()
});
var authTokensSchema = z2.object({
  accessToken: z2.string(),
  expiresIn: z2.number().int(),
  user: authUserSchema
});

// src/schemas/users.ts
import { z as z3 } from "zod";
var createUserSchema = z3.object({
  email: z3.email().trim().toLowerCase(),
  fullName: z3.string().trim().min(2).max(120),
  role: z3.enum(ROLES),
  password: passwordSchema,
  locale: z3.enum(["fr", "ar"]).default("fr"),
  customerId: z3.uuid().nullable().optional()
}).refine((u) => u.role !== "BUYER" || !!u.customerId, {
  message: "Un compte acheteur doit \xEAtre li\xE9 \xE0 un client",
  path: ["customerId"]
});
var updateUserSchema = z3.object({
  fullName: z3.string().trim().min(2).max(120).optional(),
  role: z3.enum(ROLES).optional(),
  active: z3.boolean().optional(),
  locale: z3.enum(["fr", "ar"]).optional(),
  customerId: z3.uuid().nullable().optional()
});
var resetPasswordSchema = z3.object({ password: passwordSchema });

// src/schemas/config.ts
import { z as z4 } from "zod";
var code = z4.string().trim().min(1).max(40).regex(/^[A-Za-z0-9][A-Za-z0-9 ._()/-]*$/, "Code : lettres, chiffres, espace . _ - ( ) /");
var name = z4.string().trim().min(1).max(160);
var optionalText = z4.string().trim().max(500).nullish();
var uuid = z4.uuid();
var stageSchema = z4.object({
  code: refItemFields.code,
  label: refItemFields.label,
  labelAr: refItemFields.labelAr,
  sequence: z4.coerce.number().int().min(0).max(9999),
  inputUnit: z4.enum(MEASURE_UNITS).default("KG"),
  outputUnit: z4.enum(MEASURE_UNITS).default("KG"),
  kind: z4.enum(STAGE_KINDS).default("TRANSFORM"),
  consumes: z4.array(z4.enum(MATERIAL_ROLES)).default([]),
  recordsDiameter: z4.boolean().default(false),
  /** Proposed output diameter (mm); empty = the product's wire diameter. */
  outputDiameterMm: decimalString(2).nullish(),
  sellable: z4.boolean().default(false),
  icon: z4.enum(STAGE_ICONS).default("WIRE_DRAWING"),
  active: z4.boolean().default(true)
});
var stageUpdateSchema = stageSchema.omit({ code: true });
var lineSchema = z4.object({
  code,
  name,
  active: z4.boolean().default(true),
  notes: optionalText,
  /** Ordered stages the line can perform. */
  stageCodes: z4.array(refCode).min(1, "Au moins une \xE9tape").refine((a) => new Set(a).size === a.length, "\xC9tape en double")
});
var machineSchema = z4.object({
  code,
  name,
  lineId: uuid,
  stageCode: refCode,
  active: z4.boolean().default(true),
  notes: optionalText
});
var scrapReasonSchema = z4.object({ code, label: name, active: z4.boolean().default(true) });
var containerSchema = z4.object({
  code: z4.string().trim().min(1).max(20).transform((v) => v.toUpperCase()),
  kind: z4.enum(CONTAINER_KINDS),
  tareKg: kgString,
  active: z4.boolean().default(true),
  notes: optionalText
});
var wireGaugeSchema = z4.object({
  diameterMm: decimalString(2).refine((v) => Number(v) > 0, "Diam\xE8tre > 0"),
  active: z4.boolean().default(true)
});
var materialSchema = z4.object({
  code,
  name,
  type: refCode,
  unit: z4.enum(MEASURE_UNITS),
  lowStockThreshold: kgString.default("0"),
  active: z4.boolean().default(true),
  notes: optionalText
});
var legalIds = {
  nif: z4.string().trim().max(30).nullish(),
  nis: z4.string().trim().max(30).nullish(),
  rc: z4.string().trim().max(40).nullish()
};
var supplierSchema = z4.object({
  /** Generated (FRS-0001…) when omitted, like a client's code. */
  code: code.optional(),
  name,
  ...legalIds,
  address: optionalText,
  city: z4.string().trim().max(80).nullish(),
  country: z4.string().trim().length(2).default("DZ"),
  phone: z4.string().trim().max(40).nullish(),
  email: z4.email().nullish().or(z4.literal("").transform(() => null)),
  contactName: z4.string().trim().max(120).nullish(),
  active: z4.boolean().default(true),
  notes: optionalText
});
var familySchema = z4.object({
  code,
  name,
  description: optionalText,
  conductorClass: z4.coerce.number().int().refine((v) => [1, 2, 5, 6].includes(v), "Classe 1, 2, 5 ou 6"),
  insulationMaterial: z4.enum(INSULATION_MATERIALS).nullable(),
  sheathMaterial: z4.enum(INSULATION_MATERIALS).nullable(),
  voltageRating: z4.string().trim().min(1).max(30),
  standard: z4.string().trim().max(80).nullish(),
  maxOperatingTempC: z4.coerce.number().int().min(0).max(250).nullish(),
  active: z4.boolean().default(true)
});
var productSchema = z4.object({
  /** Omitted: bare copper wire (the factory's « fil de cuivre nu » family), named by diameter. */
  familyId: uuid.nullish(),
  cores: z4.coerce.number().int().min(1).max(61).default(1),
  crossSectionMm2: decimalString(2).refine((v) => Number(v) > 0, "Section > 0").nullish(),
  colors: z4.array(z4.string().trim().min(1).max(30)).max(10).default([]),
  copperKgPerKm: kgString.refine((v) => Number(v) > 0, "Cuivre kg/km > 0").nullish(),
  compoundKgPerKm: kgString.default("0"),
  outerDiameterMm: decimalString(2).nullish(),
  // Copper wire: up to 20 mm (the rod is 8 mm) — also keeps the computed section in range.
  wireDiameterMm: decimalString(2).refine((v) => Number(v) > 0 && Number(v) <= 20, "Diam\xE8tre de fil : entre 0,01 et 20 mm").nullish(),
  wiresPerConductor: z4.coerce.number().int().min(1).max(200).default(1),
  maxConductorResistanceOhmKm: decimalString(4).nullish(),
  description: optionalText,
  active: z4.boolean().default(true),
  /** Ordered stage routing. */
  routing: z4.array(refCode).min(1, "Au moins une \xE9tape").refine((a) => new Set(a).size === a.length, "\xC9tape en double"),
  /** Optional overrides of the generated code / name. */
  code: code.optional(),
  name: name.optional()
}).refine((p) => p.crossSectionMm2 != null || p.wireDiameterMm != null, {
  message: "Section ou diam\xE8tre de fil obligatoire",
  path: ["wireDiameterMm"]
}).refine((p) => p.familyId != null || p.wireDiameterMm != null, {
  message: "Diam\xE8tre du fil obligatoire",
  path: ["wireDiameterMm"]
});
var packagingTypeSchema = z4.object({
  code,
  name,
  kind: refCode,
  nominalLengthM: meterString.nullish(),
  toleranceM: meterString.default("0"),
  tareKg: kgString.default("0"),
  active: z4.boolean().default(true),
  supplies: z4.array(z4.object({ materialId: uuid, qtyPerUnit: kgString.refine((v) => Number(v) > 0, "> 0") })).default([])
});
var skuSchema = z4.object({
  productId: uuid,
  packagingTypeId: uuid,
  portalVisible: z4.boolean().default(true),
  lowStockThreshold: z4.coerce.number().int().min(0).default(0),
  active: z4.boolean().default(true)
});
var skuUpdateSchema = skuSchema.pick({
  portalVisible: true,
  lowStockThreshold: true,
  active: true
}).partial();
var companySchema = z4.object({
  legalName: name,
  tradeName: z4.string().trim().max(160).nullish(),
  legalForm: z4.string().trim().max(40).nullish(),
  capital: decimalString(2).nullish(),
  address: z4.string().trim().min(1).max(300),
  city: z4.string().trim().min(1).max(80),
  wilaya: z4.string().trim().min(1).max(80),
  postalCode: z4.string().trim().max(10).nullish(),
  phone: z4.string().trim().min(6).max(40),
  whatsappPhone: z4.string().trim().max(40).nullish(),
  email: z4.email().nullish().or(z4.literal("").transform(() => null)),
  website: z4.string().trim().max(120).nullish(),
  nif: z4.string().trim().min(1).max(30),
  nis: z4.string().trim().min(1).max(30),
  rc: z4.string().trim().min(1).max(40),
  articleImposition: z4.string().trim().min(1).max(30),
  rib: z4.string().trim().max(40).nullish(),
  bankName: z4.string().trim().max(120).nullish(),
  invoiceFooter: z4.string().trim().max(500).nullish()
});
var pct = decimalString(2).refine((v) => Number(v) <= 100, "\u2264 100");
var prefix = z4.string().trim().min(1).max(8).regex(/^[A-Z0-9]+$/, "Majuscules / chiffres");
var appSettingsSchema = z4.object({
  defaultVatRate: pct,
  stampDutyEnabled: z4.boolean(),
  stampDutyMin: decimalString(2),
  stampDutyMax: decimalString(2).nullish(),
  copperVarianceThresholdPct: pct,
  daysOfCoverWindow: z4.coerce.number().int().min(1).max(365),
  invoicePrefix: prefix,
  creditNotePrefix: prefix,
  proformaPrefix: prefix,
  deliveryNotePrefix: prefix,
  salesOrderPrefix: prefix,
  workOrderPrefix: prefix,
  /** Number pattern of the sales documents ({PREFIX} {YEAR} {YY} {N}), see `numbering.ts`. */
  documentNumberPattern: z4.string().trim().min(3).max(40).superRefine((v, ctx) => {
    const problem = numberPatternProblem(v);
    if (problem) ctx.addIssue({ code: "custom", message: problem });
  }),
  documentNumberDigits: z4.coerce.number().int().min(1).max(8),
  defaultPaymentTermsDays: z4.coerce.number().int().min(0).max(365),
  stampDutyBrackets: z4.array(
    z4.object({
      minAmount: decimalString(2),
      maxAmount: decimalString(2).nullish(),
      ratePct: pct
    })
  ).min(1).refine(
    (b) => b.every((x, i) => {
      const next = b[i + 1];
      if (x.maxAmount != null && Number(x.maxAmount) <= Number(x.minAmount)) return false;
      if (!next) return x.maxAmount == null;
      return x.maxAmount != null && Number(x.maxAmount) === Number(next.minAmount);
    }) && Number(b[0]?.minAmount) === 0,
    "Tranches contigu\xEBs depuis 0, la derni\xE8re sans plafond"
  )
});
var materialReceiptSchema = z4.object({
  materialId: uuid,
  supplierId: uuid.nullish(),
  qty: kgString.refine((v) => Number(v) > 0, "Quantit\xE9 > 0"),
  /** Purchase price — owner only; the workshop receives without it (« prix à compléter »). */
  unitCost: decimalString(4).nullish(),
  receivedAt: z4.iso.datetime({ offset: true }).optional(),
  supplierCertRef: z4.string().trim().max(80).nullish(),
  supplierLotRef: z4.string().trim().max(80).nullish(),
  notes: optionalText
});
var materialAdjustSchema = z4.object({
  delta: z4.string().trim().regex(/^-?\d+(\.\d{1,3})?$/, "Nombre sign\xE9, 3 d\xE9cimales max").refine((v) => Number(v) !== 0, "\u2260 0"),
  reason: z4.string().trim().min(3).max(300)
});
var setupOwnerSchema = z4.object({
  email: z4.email().trim().toLowerCase(),
  fullName: z4.string().trim().min(2).max(120),
  password: z4.string().min(10, "Au moins 10 caract\xE8res").regex(/[A-Za-z]/, "Doit contenir une lettre").regex(/\d/, "Doit contenir un chiffre")
});
var paymentMethodSchema = z4.object({
  ...refItemFields,
  isCash: z4.boolean().default(false)
});
var paymentMethodUpdateSchema = paymentMethodSchema.omit({ code: true });
var packagingKindSchema = z4.object({
  ...refItemFields,
  cutToLength: z4.boolean().default(false)
});
var packagingKindUpdateSchema = packagingKindSchema.omit({ code: true });
var materialTypeSchema = z4.object({
  ...refItemFields,
  role: z4.enum(MATERIAL_ROLES),
  stageCode: refCode.nullish()
});
var materialTypeUpdateSchema = materialTypeSchema.omit({ code: true });
var wipThresholdSchema = z4.object({
  stageCode: refCode,
  /** Empty for a step without diameter. */
  diameterMm: decimalString(2).nullish(),
  thresholdKg: kgString
});
var materialPriceSchema = z4.object({ unitCost: decimalString(4) });

// src/schemas/production.ts
import { z as z5 } from "zod";
var uuid2 = z5.uuid();
var isoDate = z5.iso.datetime({ offset: true });
var workOrderSchema = z5.object({
  kind: z5.enum(["FINISHED", "STOCK"]).default("FINISHED"),
  lineId: uuid2,
  skuId: uuid2.nullish(),
  targetUnits: z5.coerce.number().int().min(1).max(1e5).nullish(),
  /** Required for cut-to-length SKUs (no nominal length); otherwise derived from the packaging. */
  targetLengthM: meterString.nullish(),
  productId: uuid2.nullish(),
  endStageCode: refCode.nullish(),
  targetKg: kgString.refine((v) => Number(v) > 0, "Poids > 0").nullish(),
  plannedStart: isoDate.nullish(),
  plannedEnd: isoDate.nullish(),
  priority: z5.coerce.number().int().min(0).max(9).default(0),
  notes: z5.string().trim().max(500).nullish(),
  /**
   * The client's order line this production is for (« pour le stock » kind): its bobbins are
   * reserved to that order. Absent = unchanged on an update; null = not for a client.
   */
  salesOrderLineId: uuid2.nullish()
}).superRefine((w, ctx) => {
  const need = (ok, path, message) => {
    if (!ok) ctx.addIssue({ code: "custom", path: [path], message });
  };
  if (w.kind === "FINISHED") {
    need(w.skuId != null, "skuId", "Article obligatoire");
    need(w.targetUnits != null, "targetUnits", "Nombre d\u2019unit\xE9s obligatoire");
  } else {
    need(w.productId != null, "productId", "Produit obligatoire");
    need(w.endStageCode != null, "endStageCode", "\xC9tape de fin obligatoire");
    need(w.targetKg != null, "targetKg", "Objectif en kg obligatoire");
  }
});
var workOrderClientSchema = z5.object({ salesOrderLineId: uuid2.nullable() });
var stageInputSchema = z5.object({
  materialLotId: uuid2.optional(),
  wipLotId: uuid2.optional(),
  /** Material: qty in the material unit; WIP: kg. */
  qty: kgString.optional(),
  /** WIP only: consume by length instead of weight. */
  lengthM: meterString.optional(),
  scrapKg: kgString.optional()
}).refine(
  (i) => (i.materialLotId ? 1 : 0) + (i.wipLotId ? 1 : 0) === 1,
  "Un lot mati\xE8re ou un lot en-cours"
).refine((i) => i.qty != null || i.wipLotId != null && i.lengthM != null, "Quantit\xE9 obligatoire").refine((i) => i.lengthM == null || i.wipLotId != null, "Longueur : lots en-cours uniquement");
var stageLogSchema = z5.object({
  workOrderId: uuid2,
  stageCode: refCode,
  machineId: uuid2.nullish(),
  startedAt: isoDate.optional(),
  endedAt: isoDate.optional(),
  inputs: z5.array(stageInputSchema).max(30),
  output: z5.object({
    /** Net kg; weighed in a container it must equal gross − the container's tare. */
    qtyKg: kgString.refine((v) => Number(v) > 0, "Poids > 0"),
    qtyM: meterString.nullish(),
    wireDiameterMm: decimalString(2).nullish(),
    containerId: uuid2.nullish(),
    grossKg: kgString.nullish(),
    /**
     * « Bobine sans numéro »: no registered container, its empty weight typed on the spot
     * (net = gross − this). Treated as a bobbin.
     */
    tareKg: kgString.nullish()
  }).refine((o) => o.grossKg == null || o.containerId != null || o.tareKg != null, {
    message: "Contenant ou poids vide de la bobine",
    path: ["grossKg"]
  }).refine((o) => o.containerId == null || o.tareKg == null, {
    message: "Le poids vide d\u2019un contenant num\xE9rot\xE9 vient de sa fiche",
    path: ["tareKg"]
  }).refine((o) => o.containerId == null && o.tareKg == null || o.grossKg != null, {
    message: "Poids brut obligatoire",
    path: ["grossKg"]
  }).nullish(),
  scrap: z5.object({
    reasonId: uuid2,
    material: z5.enum(SCRAP_MATERIALS),
    /** « Retour au déchet »: the copper scrapped goes back to the déchet stock, melted again. */
    recycle: z5.boolean().default(false)
  }).nullish(),
  notes: z5.string().trim().max(500).nullish()
}).refine((s) => !s.inputs.some((i) => Number(i.scrapKg ?? 0) > 0) || s.scrap != null, {
  message: "Motif de rebut obligatoire",
  path: ["scrap"]
});
var packSchema = z5.object({
  workOrderId: uuid2,
  sourceWipLotId: uuid2,
  /** Another packaging of the same product (e.g. cut to length); defaults to the work order's SKU. */
  skuId: uuid2.optional(),
  machineId: uuid2.nullish(),
  unitLengthsM: z5.array(meterString.refine((v) => Number(v) > 0, "Longueur > 0")).min(1).max(300).optional(),
  unitNetKg: z5.array(kgString.refine((v) => Number(v) > 0, "Poids > 0")).min(1).max(300).optional(),
  /** Short end left on the lot, scrapped: by length or by weight. */
  offcut: z5.object({ lengthM: meterString.optional(), kg: kgString.optional(), reasonId: uuid2 }).refine((o) => o.lengthM == null !== (o.kg == null), "Chute : longueur ou poids").nullish(),
  notes: z5.string().trim().max(500).nullish()
}).refine((p) => p.unitLengthsM == null !== (p.unitNetKg == null), {
  message: "Unit\xE9s : longueurs ou poids",
  path: ["unitNetKg"]
});
var qualityTestSchema = z5.object({
  wipLotId: uuid2.optional(),
  /** Several finished units can share one test record set (batch test): one QualityTest per unit. */
  finishedUnitIds: z5.array(uuid2).max(300).optional(),
  conductorResistanceOhmKm: decimalString(4).nullish(),
  insulationResistanceMOhmKm: decimalString(2).nullish(),
  voltageTestKv: decimalString(2).nullish(),
  voltageTestPassed: z5.boolean().nullish(),
  outerDiameterMm: decimalString(2).nullish(),
  insulationThicknessMm: decimalString(2).nullish(),
  result: z5.enum(QC_RESULTS),
  notes: z5.string().trim().max(500).nullish()
}).refine(
  (q) => (q.wipLotId ? 1 : 0) + (q.finishedUnitIds?.length ? 1 : 0) === 1,
  "Un lot en-cours ou des unit\xE9s"
);
var wire3mmSchema = z5.object({
  producedKg: kgString,
  usedKg: kgString.nullish(),
  /** Slag (scories) weighed out of the furnace: declared loss, not an unexplained gap. */
  slagKg: kgString.nullish(),
  /** Déchet recovered from the melt (spills, drips…): back in the déchet stock, not a loss. */
  recoveredKg: kgString.nullish(),
  /** The 3 mm material when several exist (default: the only one). */
  materialId: uuid2.nullish(),
  /**
   * Weighed basket by basket: each basket becomes its own 3 mm lot (net = gross − tare) and
   * `producedKg` must be their total. The melt's figures are shared out by net weight.
   */
  baskets: z5.array(z5.object({ containerId: uuid2, grossKg: kgString })).max(50).refine((b) => new Set(b.map((x) => x.containerId)).size === b.length, "Panier en double").nullish(),
  notes: z5.string().trim().max(500).nullish()
}).refine((v) => new Decimal(v.producedKg).gt(0), {
  path: ["producedKg"],
  message: "Le poids de fil 3 mm doit \xEAtre positif"
}).refine((v) => v.usedKg == null || new Decimal(v.usedKg).gte(v.producedKg), {
  path: ["usedKg"],
  message: "Le d\xE9chet utilis\xE9 ne peut pas \xEAtre inf\xE9rieur au cuivre obtenu"
}).refine(
  (v) => new Decimal(v.usedKg ?? v.producedKg).gte(
    new Decimal(v.producedKg).plus(v.slagKg ?? 0).plus(v.recoveredKg ?? 0)
  ),
  {
    path: ["usedKg"],
    message: "Cuivre obtenu + r\xE9cup\xE9r\xE9 + scories d\xE9passent le d\xE9chet fondu"
  }
);
var weightCorrectionSchema = z5.object({
  grossKg: kgString.nullish(),
  qtyKg: kgString.nullish(),
  reason: z5.string().trim().min(3, "Motif obligatoire").max(200)
}).refine((v) => v.grossKg == null !== (v.qtyKg == null), {
  message: "Poids brut ou poids net",
  path: ["qtyKg"]
});
var stockRemovalSchema = z5.object({
  kg: kgString.refine((v) => Number(v) > 0, "> 0"),
  reason: z5.string().trim().min(3, "Motif obligatoire").max(200)
});
var fusionCorrectionSchema = z5.object({
  producedKg: kgString.refine((v) => Number(v) > 0, "Poids > 0"),
  reason: z5.string().trim().min(3, "Motif obligatoire").max(200)
});
var copperBalanceQuerySchema = z5.object({
  from: isoDate.or(z5.iso.date()).optional(),
  to: isoDate.or(z5.iso.date()).optional(),
  lineId: uuid2.optional()
});
function shiftAt(date) {
  const hour = Number(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: TIMEZONE,
      hour: "2-digit",
      hourCycle: "h23"
    }).format(date)
  );
  return hour >= 6 && hour < 14 ? "MORNING" : hour >= 14 && hour < 22 ? "AFTERNOON" : "NIGHT";
}

// src/schemas/sales.ts
import { z as z6 } from "zod";
var uuid3 = z6.uuid();
var isoDate2 = z6.iso.datetime({ offset: true });
var text = (max) => z6.string().trim().max(max).nullish();
var positive = (s) => s.refine((v) => Number(v) > 0, "> 0");
var customerSchema = z6.object({
  /** Generated (CLI-0001…) when omitted. */
  code: z6.string().trim().min(1).max(20).optional(),
  name: z6.string().trim().min(2).max(160),
  legalForm: text(40),
  nif: text(30),
  nis: text(30),
  rc: text(40),
  articleImposition: text(30),
  address: text(300),
  city: text(80),
  wilaya: text(80),
  phone: text(40),
  email: z6.email().nullish().or(z6.literal("").transform(() => null)),
  contactName: text(120),
  paymentTermsDays: z6.coerce.number().int().min(0).max(365).default(30),
  creditLimit: moneyString.nullish(),
  active: z6.boolean().default(true),
  notes: text(500)
});
var priced = {
  unitPrice: unitPriceString,
  vatRate: pctString.refine((v) => Number(v) <= 100, "\u2264 100")
};
var salesOrderLineSchema = z6.discriminatedUnion("kind", [
  /** Coils / drums: ordered by units, or by weight (kg) — whole units are then reserved. */
  z6.object({
    kind: z6.literal("PACKAGED"),
    skuId: uuid3,
    qtyUnits: z6.coerce.number().int().min(1).nullish(),
    qtyKg: positive(kgString).nullish(),
    priceUnit: z6.enum(["PER_UNIT", "PER_METER", "PER_KG"]),
    ...priced
  }),
  z6.object({
    kind: z6.literal("CUT_LENGTH"),
    /** SKU with a CUT packaging. */
    skuId: uuid3,
    qtyUnits: z6.coerce.number().int().min(1),
    cutLengthM: positive(meterString),
    priceUnit: z6.enum(["PER_METER", "PER_UNIT"]),
    ...priced
  }),
  /** Workshop stock sold at a step: the step + its wire diameter (product optional). */
  z6.object({
    kind: z6.literal("IN_PROCESS"),
    productId: uuid3.nullish(),
    stageCode: refCode,
    wireDiameterMm: decimalString(2).nullish(),
    qtyKg: kgString.nullish(),
    qtyM: meterString.nullish(),
    priceUnit: z6.enum(["PER_KG", "PER_METER"]),
    ...priced
  })
]).refine((l) => l.kind !== "IN_PROCESS" || Number(l.qtyKg ?? 0) > 0 || Number(l.qtyM ?? 0) > 0, {
  message: "Quantit\xE9 (kg ou m) obligatoire"
}).refine((l) => l.kind !== "IN_PROCESS" || l.productId != null || l.wireDiameterMm != null, {
  message: "Diam\xE8tre ou produit obligatoire",
  path: ["wireDiameterMm"]
}).refine((l) => l.kind !== "PACKAGED" || l.qtyUnits != null !== (l.qtyKg != null), {
  message: "Quantit\xE9 : en unit\xE9s ou en kg",
  path: ["qtyUnits"]
}).refine((l) => l.kind !== "PACKAGED" || l.qtyKg == null || l.priceUnit === "PER_KG", {
  message: "Commande au kg : prix au kg",
  path: ["priceUnit"]
});
var salesOrderSchema = z6.object({
  customerId: uuid3,
  orderDate: isoDate2.optional(),
  requestedDeliveryDate: isoDate2.nullish(),
  paymentMethod: refCode.nullish(),
  notes: text(1e3),
  lines: z6.array(salesOrderLineSchema).min(1, "Au moins une ligne").max(100),
  /** Order created from this proforma (which becomes ACCEPTED). Ignored on update. */
  proformaId: uuid3.nullish()
});
var reservationSchema = z6.object({
  finishedUnitIds: z6.array(uuid3).max(500).default([]),
  wip: z6.array(z6.object({ wipLotId: uuid3, kg: positive(kgString), m: meterString.nullish() })).max(50).default([])
});
var deliveryNoteSchema = z6.object({
  salesOrderId: uuid3,
  deliveredAt: isoDate2.optional(),
  driverName: text(120),
  vehiclePlate: text(30),
  deliveryAddress: text(300),
  notes: text(500),
  finishedUnitIds: z6.array(uuid3).max(500).default([]),
  wipReservationIds: z6.array(uuid3).max(50).default([])
}).refine((d) => d.finishedUnitIds.length + d.wipReservationIds.length > 0, "Rien \xE0 livrer");
var invoiceCreateSchema = z6.object({
  deliveryNoteIds: z6.array(uuid3).min(1).max(50),
  paymentMethod: refCode,
  issueDate: isoDate2.optional(),
  notes: text(1e3)
});
var invoiceUpdateSchema = z6.object({
  paymentMethod: refCode,
  issueDate: isoDate2.optional(),
  notes: text(1e3),
  /** Unit price and VAT can be changed per line while the invoice is a draft (prices move). */
  lines: z6.array(
    z6.object({
      id: uuid3,
      vatRate: pctString.refine((v) => Number(v) <= 100, "\u2264 100").optional(),
      unitPrice: unitPriceString.optional()
    })
  ).default([])
});
var paymentSchema = z6.object({
  invoiceId: uuid3,
  amount: positive(moneyString),
  method: refCode,
  paidAt: isoDate2.optional(),
  reference: text(80),
  bankName: text(120),
  notes: text(500)
});
var creditNoteSchema = z6.object({
  invoiceId: uuid3,
  reason: z6.string().trim().min(3).max(500),
  issueDate: isoDate2.optional(),
  lines: z6.array(
    z6.object({
      invoiceLineId: uuid3,
      qty: positive(kgString),
      unitPrice: unitPriceString.optional()
    })
  ).min(1).max(100),
  /** Delivered units coming back into stock with this credit note. */
  returnUnitIds: z6.array(uuid3).max(500).default([]),
  /** Wire sold by weight coming back (« retour client »), weighed per delivered lot. */
  returnWip: z6.array(
    z6.object({
      deliveryItemId: uuid3,
      kg: positive(kgString),
      /** To the déchet (to remelt) instead of back into finished stock. */
      toScrap: z6.boolean().default(false)
    })
  ).max(200).default([])
});
var invoiceReverseSchema = z6.object({
  reason: z6.string().trim().min(3).max(500),
  returnStock: z6.boolean().default(false),
  /** With `returnStock`: the wire goes back to the déchet (to be melted again), not into its lot. */
  toScrap: z6.boolean().default(false)
});
var proformaSchema = z6.object({
  customerId: uuid3,
  validUntil: isoDate2.nullish(),
  paymentMethod: refCode.nullish(),
  notes: text(1e3),
  lines: z6.array(
    z6.object({
      skuId: uuid3.nullish(),
      description: z6.string().trim().min(1).max(300),
      priceUnit: z6.enum(["PER_UNIT", "PER_METER", "PER_KG"]),
      qty: positive(kgString),
      ...priced
    })
  ).min(1).max(100)
});

// src/schemas/reports.ts
import { z as z7 } from "zod";
var REPORT_NAMES = [
  "copper-balance",
  "production",
  "scrap",
  "stock-valuation",
  "sales",
  "receivables",
  "customer-situation",
  "materials-monthly"
];
var reportQuerySchema = z7.object({
  from: z7.iso.date().optional(),
  to: z7.iso.date().optional(),
  lineId: z7.uuid().optional(),
  groupBy: z7.enum(["day", "line", "product", "customer", "reason", "stage", "workOrder"]).optional(),
  format: z7.enum(["json", "xlsx", "pdf"]).default("json")
});
var materialsMonthlyQuerySchema = z7.object({
  from: z7.string().regex(/^\d{4}-\d{2}$/).optional(),
  to: z7.string().regex(/^\d{4}-\d{2}$/).optional()
});

// src/i18n/ar.ts
var enumLabelsAr = {
  role: {
    OWNER: "\u0627\u0644\u0645\u0627\u0644\u0643",
    MANAGER: "\u0627\u0644\u0645\u0633\u0624\u0648\u0644",
    OPERATOR: "\u0627\u0644\u0639\u0627\u0645\u0644",
    SALES: "\u0627\u0644\u0645\u0643\u0644\u0651\u0641 \u0628\u0627\u0644\u0645\u0628\u064A\u0639\u0627\u062A",
    BUYER: "\u0627\u0644\u0645\u0634\u062A\u0631\u064A"
  },
  stageKind: {
    TRANSFORM: "\u062A\u062D\u0648\u064A\u0644 (\u064A\u0646\u062A\u062C \u062F\u0641\u0639\u0629 \u0642\u064A\u062F \u0627\u0644\u062A\u0635\u0646\u064A\u0639)",
    TEST: "\u0645\u0631\u0627\u0642\u0628\u0629 (\u0644\u0627 \u062A\u0646\u062A\u062C \u062F\u0641\u0639\u0629)",
    PACK: "\u062A\u0639\u0628\u0626\u0629 (\u0645\u0646\u062A\u062C \u0646\u0647\u0627\u0626\u064A)"
  },
  stageIcon: {
    RAW: "\u0645\u0627\u062F\u0629",
    ROD_BREAKDOWN: "\u0627\u0644\u062A\u062E\u0634\u064A\u0646",
    WIRE_DRAWING: "\u0627\u0644\u0633\u062D\u0628",
    ANNEALING: "\u0627\u0644\u062A\u0644\u062F\u064A\u0646",
    STRANDING: "\u0627\u0644\u062C\u062F\u0644",
    INSULATION: "\u0627\u0644\u0639\u0632\u0644",
    CABLING: "\u0627\u0644\u062A\u062C\u0645\u064A\u0639",
    SHEATHING: "\u0627\u0644\u062A\u063A\u0644\u064A\u0641 \u0627\u0644\u062E\u0627\u0631\u062C\u064A",
    TESTING: "\u0627\u0644\u0627\u062E\u062A\u0628\u0627\u0631\u0627\u062A",
    PACKAGING: "\u0627\u0644\u062A\u0639\u0628\u0626\u0629"
  },
  materialRole: {
    COPPER: "\u0646\u062D\u0627\u0633 (\u062D\u0635\u064A\u0644\u0629 \u0627\u0644\u0646\u062D\u0627\u0633\u060C \u064A\u062F\u062E\u0644 \u0641\u064A \u0627\u0644\u0645\u0631\u062D\u0644\u0629 \u0627\u0644\u0623\u0648\u0644\u0649)",
    COMPOUND: "\u062E\u0644\u064A\u0637 \u0627\u0644\u0639\u0632\u0644 / \u0627\u0644\u063A\u0644\u0627\u0641",
    FILLER: "\u062D\u0634\u0648 (\u0627\u0644\u062A\u062C\u0645\u064A\u0639)",
    PACKAGING: "\u062A\u063A\u0644\u064A\u0641 (\u0644\u0648\u0627\u0632\u0645 \u0644\u0643\u0644 \u0648\u062D\u062F\u0629)",
    OTHER: "\u0623\u062E\u0631\u0649"
  },
  containerKind: {
    BASKET: "\u0633\u0644\u0629",
    BOBBIN: "\u0628\u0643\u0631\u0629",
    OTHER: "\u0648\u0639\u0627\u0621 \u0622\u062E\u0631"
  },
  saleKind: {
    PACKAGED: "\u0645\u0646\u062A\u062C \u0645\u0639\u0628\u0651\u0623",
    CUT_LENGTH: "\u0637\u0648\u0644 \u0646\u0647\u0627\u0626\u064A (\u0628\u0627\u0644\u0642\u0637\u0639)",
    IN_PROCESS: "\u0628\u0627\u0644\u0648\u0632\u0646"
  },
  workOrderStatus: {
    DRAFT: "\u0645\u0633\u0648\u062F\u0629",
    RELEASED: "\u0645\u064F\u0637\u0644\u0642",
    IN_PROGRESS: "\u0642\u064A\u062F \u0627\u0644\u062A\u0646\u0641\u064A\u0630",
    DONE: "\u0645\u0646\u062A\u0647\u064D",
    CANCELLED: "\u0645\u0644\u063A\u0649"
  },
  stockStatus: {
    IN_STOCK: "\u0641\u064A \u0627\u0644\u0645\u062E\u0632\u0648\u0646",
    RESERVED: "\u0645\u062D\u062C\u0648\u0632",
    DELIVERED: "\u0645\u064F\u0633\u0644\u064E\u0651\u0645",
    SCRAPPED: "\u0645\u064F\u0647\u062F\u064E\u0631",
    MISSING: "\u0645\u0641\u0642\u0648\u062F"
  },
  movementType: {
    RECEIPT: "\u0627\u0633\u062A\u0644\u0627\u0645",
    CONSUMPTION: "\u0627\u0633\u062A\u0647\u0644\u0627\u0643",
    PRODUCTION: "\u0625\u0646\u062A\u0627\u062C",
    SCRAP: "\u0647\u062F\u0631",
    ADJUSTMENT: "\u062A\u0633\u0648\u064A\u0629",
    RESERVATION: "\u062D\u062C\u0632",
    RESERVATION_RELEASE: "\u0625\u0644\u063A\u0627\u0621 \u0627\u0644\u062D\u062C\u0632",
    DELIVERY: "\u062A\u0633\u0644\u064A\u0645",
    RETURN: "\u0625\u0631\u062C\u0627\u0639",
    REVALUATION: "\u0633\u0639\u0631 \u0627\u0644\u0634\u0631\u0627\u0621 (\u0625\u0639\u0627\u062F\u0629 \u062A\u0642\u064A\u064A\u0645)"
  }
};

// src/i18n/fr.ts
var enumLabelsFr = {
  role: {
    OWNER: "Propri\xE9taire",
    MANAGER: "Responsable",
    OPERATOR: "Op\xE9rateur",
    SALES: "Commercial",
    BUYER: "Acheteur"
  },
  stageKind: {
    TRANSFORM: "Transformation (produit un lot en-cours)",
    TEST: "Contr\xF4le (pas de lot produit)",
    PACK: "Conditionnement (produit fini)"
  },
  stageIcon: {
    RAW: "Mati\xE8re",
    ROD_BREAKDOWN: "D\xE9grossissage",
    WIRE_DRAWING: "Tr\xE9filage",
    ANNEALING: "Recuit",
    STRANDING: "Toronnage",
    INSULATION: "Isolation",
    CABLING: "Assemblage",
    SHEATHING: "Gainage",
    TESTING: "Essais",
    PACKAGING: "Conditionnement"
  },
  materialRole: {
    COPPER: "Cuivre (bilan cuivre, entre \xE0 la premi\xE8re \xE9tape)",
    COMPOUND: "M\xE9lange d\u2019isolation / gaine",
    FILLER: "Bourrage (assemblage)",
    PACKAGING: "Emballage (fournitures par unit\xE9)",
    OTHER: "Autre"
  },
  containerKind: {
    BASKET: "Panier",
    BOBBIN: "Bobine",
    OTHER: "Autre contenant"
  },
  saleKind: {
    PACKAGED: "Produit conditionn\xE9",
    CUT_LENGTH: "Longueur finie (\xE0 la coupe)",
    IN_PROCESS: "Au poids"
  },
  workOrderStatus: {
    DRAFT: "Brouillon",
    RELEASED: "Lanc\xE9",
    IN_PROGRESS: "En cours",
    DONE: "Termin\xE9",
    CANCELLED: "Annul\xE9"
  },
  stockStatus: {
    IN_STOCK: "En stock",
    RESERVED: "R\xE9serv\xE9",
    DELIVERED: "Livr\xE9",
    SCRAPPED: "Rebut\xE9",
    MISSING: "Manquant"
  },
  movementType: {
    RECEIPT: "R\xE9ception",
    CONSUMPTION: "Consommation",
    PRODUCTION: "Production",
    SCRAP: "Rebut",
    ADJUSTMENT: "Ajustement",
    RESERVATION: "R\xE9servation",
    RESERVATION_RELEASE: "Lib\xE9ration de r\xE9servation",
    DELIVERY: "Livraison",
    RETURN: "Retour",
    REVALUATION: "Prix d\u2019achat (revalorisation)"
  }
};

// src/schemas/notifications.ts
import { z as z8 } from "zod";
var pushSubscriptionSchema = z8.object({
  endpoint: z8.url().max(1e3),
  keys: z8.object({
    p256dh: z8.string().min(20).max(200),
    auth: z8.string().min(8).max(100)
  })
});
var pushEndpointSchema = z8.object({ endpoint: z8.url().max(1e3) });

// src/schemas/stock-count.ts
import { z as z9 } from "zod";
var stockCountCreateSchema = z9.object({
  kinds: z9.array(z9.enum(STOCK_ITEM_KINDS)).min(1, "Choisissez au moins un type de stock"),
  materialIds: z9.array(z9.uuid()).max(200).default([]),
  productIds: z9.array(z9.uuid()).max(200).default([]),
  notes: z9.string().trim().max(500).nullish()
});
var stockCountEntrySchema = z9.object({
  code: z9.string().trim().min(3).max(60),
  qty: kgString.optional(),
  lengthM: meterString.optional(),
  note: z9.string().trim().max(300).nullish()
});
var stockCountListQuerySchema = z9.object({
  status: z9.enum(STOCK_COUNT_STATUSES).optional(),
  ...pageFields
});

// src/wire.ts
var COPPER_DENSITY = new Decimal("8.89");
function wireSectionMm2(diameterMm, wires = 1) {
  const d = new Decimal(diameterMm);
  return d.times(d).times(Math.PI).div(4).times(wires).toDecimalPlaces(2);
}
function copperKgPerKm(sectionMm2, cores = 1) {
  return roundKg(new Decimal(sectionMm2).times(cores).times(COPPER_DENSITY));
}
export {
  CONTAINER_KINDS,
  COPPER_DENSITY,
  CURRENCY,
  DATETIME_FORMAT,
  DATE_FORMAT,
  DEFAULT_COPPER_VARIANCE_THRESHOLD_PCT,
  DEFAULT_LOCALE,
  DEFAULT_NUMBER_DIGITS,
  DEFAULT_NUMBER_PATTERN,
  DEFAULT_PAGE_SIZE,
  DEFAULT_VAT_RATE,
  DOCUMENT_STATUSES,
  Decimal,
  INSULATION_MATERIALS,
  MATERIAL_ROLES,
  MEASURE_UNITS,
  MOVEMENT_TYPES,
  PAYMENT_STATUSES,
  PORTAL_ROLES,
  PRICE_UNITS,
  PROFORMA_STATUSES,
  QC_RESULTS,
  QC_STATUSES,
  REPORT_NAMES,
  ROLES,
  RTL_LOCALES,
  SALES_ORDER_STATUSES,
  SALE_KINDS,
  SCALE,
  SCRAP_MATERIALS,
  SHIFTS,
  STAFF_ROLES,
  STAGE_ICONS,
  STAGE_KINDS,
  STOCK_COUNT_OUTCOMES,
  STOCK_COUNT_STATUSES,
  STOCK_ITEM_KINDS,
  STOCK_STATUSES,
  SUPPORTED_LOCALES,
  TIMEZONE,
  WIP_LOT_STATUSES,
  WORK_ORDER_STATUSES,
  appSettingsSchema,
  authTokensSchema,
  authUserSchema,
  changePasswordSchema,
  companySchema,
  containerSchema,
  copperBalanceQuerySchema,
  copperKgPerKm,
  createUserSchema,
  creditNoteSchema,
  customerSchema,
  decimalString,
  deliveryNoteSchema,
  enumLabelsAr,
  enumLabelsFr,
  familySchema,
  formatDate,
  formatDateTime,
  formatDecimalFr,
  formatDzd,
  formatKg,
  formatM,
  formatNumberPattern,
  fusionCorrectionSchema,
  idSchema,
  inProcessLabel,
  invoiceCreateSchema,
  invoiceReverseSchema,
  invoiceUpdateSchema,
  kgString,
  lineSchema,
  loginSchema,
  machineSchema,
  materialAdjustSchema,
  materialPriceSchema,
  materialReceiptSchema,
  materialSchema,
  materialTypeSchema,
  materialTypeUpdateSchema,
  materialsMonthlyQuerySchema,
  meterString,
  moneyString,
  numberPatternProblem,
  packSchema,
  packagingKindSchema,
  packagingKindUpdateSchema,
  packagingTypeSchema,
  pageFields,
  paginationQuerySchema,
  passwordSchema,
  paymentMethodSchema,
  paymentMethodUpdateSchema,
  paymentSchema,
  pctString,
  productSchema,
  proformaSchema,
  pushEndpointSchema,
  pushSubscriptionSchema,
  qualityTestSchema,
  refCode,
  refItemFields,
  reportQuerySchema,
  reservationSchema,
  resetPasswordSchema,
  round,
  roundKg,
  roundM,
  roundMoney,
  salesOrderLineSchema,
  salesOrderSchema,
  scrapReasonSchema,
  setupOwnerSchema,
  shiftAt,
  skuSchema,
  skuUpdateSchema,
  stageInputSchema,
  stageLogSchema,
  stageSchema,
  stageUpdateSchema,
  stockCountCreateSchema,
  stockCountEntrySchema,
  stockCountListQuerySchema,
  stockRemovalSchema,
  sum,
  supplierSchema,
  toFixedString,
  unitPriceString,
  updateUserSchema,
  weightCorrectionSchema,
  wipThresholdSchema,
  wire3mmSchema,
  wireGaugeSchema,
  wireSectionMm2,
  workOrderClientSchema,
  workOrderSchema
};
//# sourceMappingURL=index.js.map